import * as THREE from 'three';
import {
  INTERACTIVE_USERDATA_KEY,
  MESH_NAME_QUILL,
  QUILL_ASPECT,
  QUILL_LENGTH_M,
  QUILL_LIFT_M,
  QUILL_OPACITY,
  UI_TEXTURE_QUILL_URL,
} from '../config/constants';

/**
 * Menu pointer (#39): a quill feather whose nib rests on the interactive panel at the
 * point the controller aims — the feather sits where the old ink dot used to be, so it
 * *is* the cursor. Nothing is held in the user's hand and there is no separate dot.
 *
 * The quill is billboarded toward the camera every frame (see `updateMenuPointer`):
 * a plane pivoted at its writing tip, faced at the viewer so it never collapses
 * edge-on (the cause of the invisible / squished feather) and reads the same on the
 * headset and in the desktop emulator.
 *
 * Module singleton (mirrors the decal helpers): one instance lives for the whole
 * immersive session. Panels `acquire`/`release` it instead of attaching lasers,
 * and the render loop drives `update` each frame.
 */

type ControllerPointer = {
  controller: THREE.Object3D;
  /** Feather whose nib rests on the panel where the controller points. */
  quill: THREE.Mesh;
};

let scene: THREE.Scene | null = null;
let pointers: ControllerPointer[] = [];
let quillTexture: THREE.Texture | null = null;
/** Number of open menus holding the pointer; quills may show while > 0. */
let active = 0;

const raycaster = new THREE.Raycaster();
const tmpOrigin = new THREE.Vector3();
const tmpDir = new THREE.Vector3();
const tmpNormal = new THREE.Vector3();
const tmpQuat = new THREE.Quaternion();

/** Create the quills in the scene, tracking the controllers (hidden until aiming at a panel). */
export function initMenuPointer(
  targetScene: THREE.Scene,
  controllerA: THREE.Object3D,
  controllerB: THREE.Object3D,
): void {
  if (scene) disposeMenuPointer();
  scene = targetScene;
  quillTexture = new THREE.TextureLoader().load(UI_TEXTURE_QUILL_URL);
  quillTexture.colorSpace = THREE.SRGBColorSpace;
  pointers = [makePointer(controllerA), makePointer(controllerB)];
}

/**
 * Increment the open-menu count. Quills then appear per-frame wherever a controller
 * aims at a panel. Pair with `releaseMenuPointer`.
 */
export function acquireMenuPointer(): void {
  active += 1;
}

/** Decrement the open-menu count; hide the quills once no menu is open. */
export function releaseMenuPointer(): void {
  active = Math.max(0, active - 1);
  if (active > 0) return;
  for (const p of pointers) p.quill.visible = false;
}

/**
 * Per-frame: rest each quill's nib on the interactive panel where its controller aims,
 * billboarded to face the camera. A quill with no panel hit is hidden.
 */
export function updateMenuPointer(camera: THREE.Camera): void {
  if (!scene || active === 0) return;
  camera.getWorldQuaternion(tmpQuat);
  for (const p of pointers) {
    const hit = raycastInteractive(p.controller, scene);
    if (!hit) {
      p.quill.visible = false;
      continue;
    }
    placeQuill(p.quill, hit);
    p.quill.visible = true;
  }
}

/** Detach + dispose everything. Safe to call when nothing is initialised. */
export function disposeMenuPointer(): void {
  for (const p of pointers) {
    scene?.remove(p.quill);
    p.quill.geometry.dispose();
    (p.quill.material as THREE.Material).dispose();
  }
  quillTexture?.dispose();
  quillTexture = null;
  pointers = [];
  active = 0;
  scene = null;
}

function makePointer(controller: THREE.Object3D): ControllerPointer {
  const quill = createQuill();
  scene!.add(quill);
  return { controller, quill };
}

/** Textured plane, pivoted at its writing tip; `placeQuill` rests that tip on the panel. */
function createQuill(): THREE.Mesh {
  const height = QUILL_LENGTH_M;
  const width = height * QUILL_ASPECT;
  const geom = new THREE.PlaneGeometry(width, height);
  // Pivot at the writing tip (image bottom) so the plume rises from the panel hit point.
  geom.translate(0, height / 2, 0);
  const mat = new THREE.MeshBasicMaterial({
    map: quillTexture,
    transparent: true,
    opacity: QUILL_OPACITY,
    side: THREE.DoubleSide,
    depthWrite: false,
    // Draw over the panel it rests on (no z-fighting), like the ink dot it replaces.
    depthTest: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = MESH_NAME_QUILL;
  mesh.renderOrder = 1000;
  mesh.visible = false;
  // Never a raycast target for the picker or for this module's own ray.
  mesh.raycast = () => {};
  return mesh;
}

/** First interactive mesh hit by the controller's forward (−Z) ray, or null. */
function raycastInteractive(
  controller: THREE.Object3D,
  root: THREE.Object3D,
): THREE.Intersection | null {
  controller.updateMatrixWorld(true);
  tmpOrigin.setFromMatrixPosition(controller.matrixWorld);
  tmpDir.set(0, 0, -1).transformDirection(controller.matrixWorld).normalize();
  raycaster.set(tmpOrigin, tmpDir);
  const intersects = raycaster.intersectObject(root, true);
  for (const hit of intersects) {
    if (hit.object.userData[INTERACTIVE_USERDATA_KEY]) return hit;
  }
  return null;
}

/** Rest the quill's nib on the panel at the hit point, lifted clear and billboarded to the camera. */
function placeQuill(quill: THREE.Mesh, hit: THREE.Intersection): void {
  quill.position.copy(hit.point);
  if (hit.face) {
    tmpNormal.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
    quill.position.addScaledVector(tmpNormal, QUILL_LIFT_M);
  }
  // Face the viewer so the feather never collapses edge-on; its tip stays on the panel.
  quill.quaternion.copy(tmpQuat);
}
